<template>
  <fieldset>
    <div v-if="showBasicWarningMessage" class="form-group">
      <div class="col-sm-offset-3 col-sm-5">
        <div class="bs-callout bs-callout-sm bs-callout-warning m-t-0 m-b-0">{{ $t("PING_TREE_TARGET_BASIC_WILL_WIPE_ADVANCED_WARNING") }}</div>
      </div>
    </div>
    <HttpRequest
      :has-auth="true"
      :request-status="requestSettingsRequestStatus.status"
      :request-settings="currentRequestSettings"
      :enable-multi-line-url="true"
      :enable-macros="enableMacros"
      @runHttpRequest="runRequest"
      @updateRequestSettings="updateHttpRequestSettings"
    />
    <HttpRequestResponse
      :request-response-body="requestResponseBody"
      :request-error="requestError"
      :request-response-code="requestResponseCode"
      :request-status="requestSettingsRequestStatus.status"
      :completed-request="completedRequest"
    />
    <RingTreeTargetParsingSettings
      v-if="showParsingSettings"
      :isDynamicNumberEnabled="isDynamicNumberEnabled"
      :isDynamicDurationEnabled="isDynamicDurationEnabled || showAllParsingSettings"
      :isDynamicRevenueEnabled="isDynamicRevenueEnabled || showAllParsingSettings"
      :acceptance-parsing.sync="acceptanceParsing"
      :dynamic-bid-parsing.sync="dynamicBidParsing"
      :dynamic-number-parsing.sync="dynamicNumberParsing"
      :dynamic-duration-parsing.sync="dynamicDurationParsing"
      :selectable-properties-config="selectablePropertiesConfig"
      @updateAcceptanceParsingSettings="updateAcceptanceParsingSettings"
      @updateDynamicDurationParsingSettings="updateDynamicDurationParsingSettings"
      @updateDynamicNumberParsingSettings="updateDynamicNumberParsingSettings"
      @updateDynamicBidParsingSettings="updateDynamicBidParsingSettings"
      @removeAcceptanceParsingStep="removeAcceptanceParsingStep"
    />
    <div class="form-group">
      <label class="col-xs-12 col-sm-3 col-md-3 control-label"> {{ $t("PING_TREE_TARGET_SECOND_STEP_ENABLE")}}
        <a v-tooltip.right="$t('PING_TREE_TARGET_SECOND_STEP_ENABLE_TT')" tooltip-placement="right"
          tooltip-class="tooltip-white" tooltip-append-to-body="true">
          &nbsp;&nbsp;<i class="fa fa-question-circle"></i>
        </a>
      </label>
      <div class="col-sm-2 col-md-1 col-xs-3 toggle-wrap">
        <nzToggle v-model="secondStepEnabledValue" @onToggle="secondStepEnabledChange">
        </nzToggle>
      </div>
    </div>
    <div class="form-group" v-if="secondStepEnabled">
      <label class="col-xs-12 col-sm-3 col-md-3 control-label"> {{ $t("PING_TREE_TARGET_BID_ID_REQUIRED")}}
        <a v-tooltip.right="$t('PING_TREE_TARGET_BID_ID_REQUIRED_TT')" tooltip-placement="right"
          tooltip-class="tooltip-white" tooltip-append-to-body="true">
          &nbsp;&nbsp;<i class="fa fa-question-circle"></i>
        </a>
      </label>
      <div class="col-sm-2 col-md-1 col-xs-3 toggle-wrap">
        <nzToggle v-model="bidIdRequiredValue" @onToggle="bidIdRequiredChange">
        </nzToggle>
      </div>
    </div>
    <BidIdParsingSettings
      :enable-bid-id-parsing="bidIdRequired && secondStepEnabled"
      :bid-id-parsing.sync="bidIdParsing"
      :selectable-properties-config="selectablePropertiesConfig"
      @updateBidIdParsingSettings="updateBidIdParsingSettings"
    />
    </fieldset>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { STATUS_SUCCESS, STATUS_PENDING, STATUS_ERROR } from '@/store/helpers';
import messages from '../locales';
import sharedMessages from '@/core/locales';
import HttpRequest from '@/core/components/HttpForm/HttpRequest';
import HttpRequestResponse from '@/core/components/HttpForm/HttpRequest/HttpRequestResponse';
import RingTreeTargetParsingSettings from './RingTreeTargetParsingSettings.vue';
import BidIdParsingSettings from './BidIdParsingSettings.vue'
import nzToggle from '@/core/components/nzToggle/nzToggleSlim.vue';
import { extractProperties } from '@/modules/dynamicInboundCalls/helpers/customScoring';
import { sanitizeAcceptanceParsingForRingTreeTargetForm, sanitizeRequestPropertyForRingTreeTargetForm } from '../helpers/requestSettings';
import { defaultParsingStep } from '../helpers/constants';

export default {
  name: 'RequestSettings',
  components: {
    HttpRequest,
    HttpRequestResponse,
    RingTreeTargetParsingSettings,
    BidIdParsingSettings,
    nzToggle
  },
  props: {
    isModal: {
      type: Boolean,
      default: false
    },
    isDynamicNumberEnabled: {
      type: Boolean,
      default: false
    },
    isDynamicRevenueEnabled: {
      type: Boolean,
      default: false
    },
    isDynamicDurationEnabled: {
      type: Boolean,
      default: false
    },
    acceptanceParsing: {
      type: Array,
      default: () => {
        return [{ ...defaultParsingStep }];
      }
    },
    dynamicNumberParsing: {
      type: Array,
      default: () => {
        return [{ ...defaultParsingStep }];
      }
    },
    dynamicBidParsing: {
      type: Array,
      default: () => {
        return [{ ...defaultParsingStep }];
      }
    },
    dynamicDurationParsing: {
      type: Array,
      default: () => {
        return [{ ...defaultParsingStep }];
      }
    },
    bidIdParsing: {
      type: Array,
      default: () => {
        return [{ ...defaultParsingStep }];
      }
    },
    bidIdRequired: {
      type: Boolean,
      default: false
    },
    secondStepEnabled: {
      type: Boolean,
      default: false
    },
    currentRequestSettings: {
      type: Object,
      default: () => ({
        url: '',
        method: 'get',
        body: '',
        authentication: '',
        headers: {},
        contentType: 'application/json'
      })
    },
    showAllParsingSettings: {
      type: Boolean,
      default: false
    },
    showBasicWarningMessage: {
      type: Boolean,
      default: false
    },
    enableMacros: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      bidIdRequiredValue: this.bidIdRequired,
      secondStepEnabledValue: this.secondStepEnabled,
      requestResponseBody: null,
      requestError: null,
      requestResponseCode: null,
      completedRequest: null,
      selectablePropertiesConfig: {
        options: [],
        placeholder: 'Choose Property',
      },
    };
  },
  computed: {
    ...mapGetters('ringTreeTargets', ['requestSettingsRequestStatus']),

    requestSettingsRequestLoading() {
      return this.requestSettingsRequestStatus.status == STATUS_PENDING;
    },
    requestSettingsRequestSuccess() {
      return this.requestSettingsRequestStatus.status == STATUS_SUCCESS;
    },
    requestSettingsRequestFail() {
      return this.requestSettingsRequestStatus.status == STATUS_ERROR;
    },
    showParsingSettings() {
      return (this.acceptanceParsing[0]?.property?.length || this.dynamicBidParsing[0]?.property?.length || this.dynamicNumberParsing[0]?.property?.length || this.dynamicDurationParsing[0]?.property?.length || this.requestSettingsRequestSuccess || this.showAllParsingSettings) && !this.requestSettingsRequestLoading;
    },
  },
  watch: {
    'secondStepEnabled': {
      immediate: true,
      async handler(newValue) {
        if (this.secondStepEnabledValue != newValue) {
          this.secondStepEnabledValue = newValue;
        }
      }
    },
    'bidIdRequired': {
      immediate: true,
      async handler(newValue) {
        if (this.bidIdRequiredValue != newValue) {
          this.bidIdRequiredValue = newValue;
        }
      }
    }
  },
  destroyed() {
    this.resetRequestSettingsRequest();
  },
  methods: {
    ...mapActions('ringTreeTargets', ['requestSettingsRequest', 'resetRequestSettingsRequest']),
    async runRequest(payload) {
      this.resetResponseData();
      const requestPayload = {
        ...payload.requestSettings,
        url: payload.completedRequestUrl,
        body: JSON.stringify(payload.completedRequestBody)
      }
      try {
        const response = await this.requestSettingsRequest(requestPayload);
        this.requestResponseCode = response.responseCode;
        this.completedRequest = {
          url: requestPayload.url,
          body: requestPayload.body,
          method: requestPayload.method
        }
        if (this.requestSettingsRequestSuccess) { 
          this.requestResponseBody = JSON.parse(response.body);
          if (!this.requestResponseBody) {
            this.requestError = this.$t('PING_TREE_TARGET_REQUEST_SETTINGS_EMPTY_RESPONSE');
            return;
          }
          const propertiesArray = extractProperties(this.requestResponseBody);
          this.selectablePropertiesConfig.options = propertiesArray;
        } else if (this.requestSettingsRequestFail) {
          this.requestError = this.requestSettingsRequestStatus.error;
          this.requestResponseBody = JSON.parse(response.body);
        }
      } catch {
        this.requestError = this.requestSettingsRequestStatus.error;
      }
    },
    resetResponseData() {
      this.requestResponseBody = null;
      this.requestError = null;
      this.requestResponseCode = null;
      this.completedRequest = null;
    },
    updateAcceptanceParsingSettings() {
      const parsingFormattedForRttForm = sanitizeAcceptanceParsingForRingTreeTargetForm(this.acceptanceParsing);

      this.$emit('acceptanceParsingChange', parsingFormattedForRttForm);
    },
    updateDynamicNumberParsingSettings() {
      const parsingFormattedForRttForm = sanitizeRequestPropertyForRingTreeTargetForm(this.dynamicNumberParsing);

      this.$emit('dynamicNumberParsingChange', parsingFormattedForRttForm);
    },
    updateDynamicBidParsingSettings() {
      const parsingFormattedForRttForm = sanitizeRequestPropertyForRingTreeTargetForm(this.dynamicBidParsing);

      this.$emit('dynamicBidParsingChange', parsingFormattedForRttForm);
    },
    updateDynamicDurationParsingSettings() {
      const parsingFormattedForRttForm = sanitizeRequestPropertyForRingTreeTargetForm(this.dynamicDurationParsing);

      this.$emit('dynamicDurationParsingChange', parsingFormattedForRttForm);
    },
    removeAcceptanceParsingStep(index) {
      this.acceptanceParsing.splice(index, 1);

      this.updateAcceptanceParsingSettings();
    },
    updateHttpRequestSettings(payload) {
      this.$emit('updateHttpRequestSettings', payload)
    },
    updateBidIdParsingSettings() {
      const parsingFormattedForRttForm = sanitizeRequestPropertyForRingTreeTargetForm(this.bidIdParsing);

      this.$emit('bidIdParsingChange', parsingFormattedForRttForm);
    },
    secondStepEnabledChange(payload) {
      this.secondStepEnabledValue = !this.secondStepEnabledValue;

      this.$emit('secondStepEnabledChange', payload);
    },
    bidIdRequiredChange(payload) {
      this.bidIdRequiredValue = !this.bidIdRequiredValue;

      this.$emit('bidIdRequiredChange', payload);
    }
  },
  i18n: {
    messages,
    sharedMessages,
    locale: 'en',
  },
};
</script>